import { X, Trophy, Volume2 } from 'lucide-react';

import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';

import {
  RARITY_STYLES,
  RARITY_BG,
  RARITY_LABEL_COLORS,
} from '@/constants/badge';

/**
 * Badge Detail Modal Component
 *
 * Shows detailed information about a badge including:
 * - Large emoji icon with rarity styling
 * - Name with audio button
 * - Rarity label
 * - Description
 * - Earned status or progress toward earning
 */
const BadgeDetailModal = ({ badge, onClose, onPlayAudio }) => {
  if (!badge) return null;

  const rarityLabel = badge.rarity?.charAt(0).toUpperCase() + badge.rarity?.slice(1);
  const isSecret = badge.is_secret && !badge.earned;

  // FIX: Show audio for earned badges including earned secret badges
  const showAudio = badge.audio_url && (!badge.is_secret || badge.earned);

  // Calculate progress
  const progressPercentage = badge.progress?.percentage ??
    (badge.progress?.current && badge.progress?.target
      ? (badge.progress.current / badge.progress.target) * 100
      : null);

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <Card className="w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
        <CardContent className="p-6 text-center relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>

          {/* Badge icon with rarity styling */}
          <div
            className={`
              inline-flex items-center justify-center w-24 h-24 rounded-2xl mb-4
              ${badge.earned
                ? `${RARITY_BG[badge.rarity] || RARITY_BG.common} ${RARITY_STYLES[badge.rarity] || RARITY_STYLES.common}`
                : 'bg-gray-100 border-2 border-dashed border-gray-200'
              }
            `.trim()}
          >
            <span
              className={`text-5xl ${!badge.earned ? 'grayscale opacity-50' : ''}`}
            >
              {isSecret ? '❓' : badge.icon_emoji}
            </span>
          </div>

          {/* Name with audio button */}
          <div className="flex items-center justify-center gap-2 mb-2">
            <h3 className="text-xl font-bold">
              {isSecret ? '???' : badge.name}
            </h3>
            {showAudio && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onPlayAudio(badge.audio_url);
                }}
                className="p-1.5 rounded-full bg-purple-100 hover:bg-purple-200 text-purple-600 transition-colors"
                title="Play pronunciation"
              >
                <Volume2 className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Rarity label */}
          <span
            className={`
              inline-block px-3 py-1 rounded-full text-xs font-medium border mb-3
              ${RARITY_LABEL_COLORS[badge.rarity] || RARITY_LABEL_COLORS.common}
            `.trim()}
          >
            {rarityLabel}
          </span>

          {/* Description */}
          <p className="text-gray-600 mb-4">
            {isSecret
              ? 'This is a secret badge. Keep practicing to discover it!'
              : badge.description}
          </p>

          {/* Earned info */}
          {badge.earned ? (
            <div className="bg-green-50 rounded-lg p-3 mb-4 border border-green-200">
              <Trophy className="h-5 w-5 text-green-600 mx-auto mb-1" />
              <p className="text-sm font-medium text-green-800">
                Earned on {new Date(badge.earned_at).toLocaleDateString()}
              </p>
              <p className="text-xs text-green-600 mt-1">
                +{badge.points_reward} points awarded
              </p>
            </div>
          ) : progressPercentage !== null && !isSecret ? (
            /* Progress display for locked badges */
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Progress</span>
                <span className="font-medium text-purple-600">
                  {progressPercentage.toFixed(0)}%
                </span>
              </div>
              <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-purple-500 rounded-full transition-all duration-300"
                  style={{ width: `${Math.min(progressPercentage, 100)}%` }}
                />
              </div>
              {badge.progress?.current !== undefined && badge.progress?.target !== undefined && (
                <p className="text-xs text-gray-500 mt-1">
                  {badge.progress.current} / {badge.progress.target}
                </p>
              )}
              <p className="text-xs text-purple-600 mt-2">
                Earn {badge.points_reward} points when unlocked!
              </p>
            </div>
          ) : !badge.earned && !isSecret ? (
            /* Not started info */
            <div className="mb-4 text-sm text-gray-500">
              <p>Keep practicing to unlock this badge!</p>
              <p className="text-xs text-purple-600 mt-1">
                Earn {badge.points_reward} points when unlocked
              </p>
            </div>
          ) : null}

          <Button variant="outline" onClick={onClose} className="w-full">
            Close
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default BadgeDetailModal;
